// G.2 — Linked Trips panel + searchable deal picker.
//
// Renders on the deal detail. Forwarder-only. Each row = the OTHER leg
// with route + dates + stage + link note.
import React, { useMemo, useState } from "react";
import {
  Link2, Plus, Trash2, X, Search, Loader2, ArrowRight,
  MapPin, Clock, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  useFreightLinks, useCreateFreightLink, useDeleteFreightLink,
} from "../api/freightLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #0a0a0a)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.15)",
  color: "#0a0a0a",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "8px 10px",
  borderRadius: 5,
  width: "100%",
};

const STAGE_COLOR = {
  registered: "#94a3b8", priced: "#0891b2", offered: "#7e3aa8",
  won: "#0a8754", executed: "#0d6b39", closed: "#475569",
};

const StagePill = ({ stage }) => {
  const c = STAGE_COLOR[stage] || "#6b7388";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded"
      style={{ background: `${c}15`, color: c, border: `1px solid ${c}55`, ...mono(9, c, 700, "0.6px"), textTransform: "uppercase" }}
    >
      {stage || "—"}
    </span>
  );
};

const _fmtRoute = (other) => {
  const pu = other?.pickup, dr = other?.dropoff;
  if (!pu && !dr) return "—";
  const a = [pu?.country, pu?.city, pu?.postal_code].filter(Boolean).join(" ");
  const b = [dr?.country, dr?.city, dr?.postal_code].filter(Boolean).join(" ");
  return `${a || "?"} → ${b || "?"}`;
};

const _fmtDate = (s) => {
  if (!s) return null;
  return String(s).slice(0, 10);
};

/* ─── Searchable deal picker ───────────────────────────────────────── */

const DealPicker = ({ excludeId, onPick, onClose }) => {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["logistics-search", q],
    queryFn: () =>
      axios.get(`${API}/logistics-requests`, {
        params: { q: q || undefined, limit: 30 },
      }).then((r) => r.data),
    // Debounce: only fire when the user pauses typing OR opens the picker.
    enabled: true,
  });
  const items = useMemo(
    () => (data?.items || []).filter((d) => d.id !== excludeId).slice(0, 25),
    [data, excludeId],
  );

  return (
    <div className="rounded p-3" style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.30)" }} data-testid="deal-picker">
      <div className="flex items-center gap-2 mb-2">
        <Search size={14} color="#7e3aa8" />
        <strong style={mono(11, "#7e3aa8", 700, "0.6px")}>FIND THE OTHER LEG</strong>
        <button onClick={onClose} className="ml-auto" style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6b7388" }}><X size={14} /></button>
      </div>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by deal id · name · customer name…"
        style={inputStyle}
        data-testid="deal-picker-search"
      />
      <div className="mt-2 max-h-72 overflow-y-auto">
        {isLoading && <div className="p-2" style={mono(11, "#6b7388")}><Loader2 size={11} className="animate-spin inline mr-1" /> Searching…</div>}
        {!isLoading && items.length === 0 && (
          <div className="p-3 text-center" style={mono(11, "#6b7388")}>No deals match. Try a different search.</div>
        )}
        {items.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onPick(d)}
            className="w-full text-left rounded p-2 mb-1"
            style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.10)", cursor: "pointer", display: "block" }}
            data-testid={`deal-picker-row-${d.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <strong style={mono(12)}>{d.deal_name || d.id}</strong>
              <StagePill stage={d.deal_stage} />
              <span style={mono(9, "#6b7388")}>{d.id}</span>
            </div>
            <div style={mono(10, "#6b7388")}>
              <MapPin size={9} className="inline mr-0.5" />
              {_fmtRoute({ pickup: (d.pickups || [{}])[0], dropoff: (d.dropoffs || [{}])[0] })}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── Linked trip row ───────────────────────────────────────────────── */

const LinkRow = ({ link }) => {
  const del = useDeleteFreightLink();
  const other = link.other || {};
  const remove = async () => {
    if (!window.confirm("Remove this link? Both deals stay; only the link goes.")) return;
    try { await del.mutateAsync(link.id); toast.success("Link removed"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };
  return (
    <div className="rounded p-3 mb-2" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.10)" }} data-testid={`freight-link-row-${link.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Link2 size={12} color="#7e3aa8" />
        <a
          href={`/logistics-requests/${other.id}`}
          target="_blank"
          rel="noreferrer"
          style={{ ...mono(12, "#7e3aa8", 700), textDecoration: "none", cursor: "pointer" }}
          data-testid={`freight-link-other-${other.id}`}
        >
          {other.deal_name || other.id}
        </a>
        <StagePill stage={other.deal_stage} />
        <span style={{ ...mono(9, "#6b7388", 700, "0.6px"), textTransform: "uppercase", background: "rgba(126,58,168,0.10)", padding: "2px 6px", borderRadius: 3 }}>
          {link.link_type}
        </span>
        <button onClick={remove} type="button" className="ml-auto" style={{ background: "transparent", border: "none", color: "#b3261e", cursor: "pointer", padding: 4 }} data-testid={`freight-link-delete-${link.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
      <div style={mono(10, "#6b7388")} className="flex flex-wrap items-center gap-3">
        <span><MapPin size={9} className="inline mr-0.5" /> {_fmtRoute(other)}</span>
        {other.request_type && <span>type: {other.request_type}</span>}
        {_fmtDate(other.pickup?.date) && <span><Clock size={9} className="inline mr-0.5" /> PU {_fmtDate(other.pickup?.date)}</span>}
        {_fmtDate(other.dropoff?.date) && <span>DO {_fmtDate(other.dropoff?.date)}</span>}
        {other.incoterm && <span style={mono(9, "#7e3aa8", 700, "0.6px")}>{other.incoterm}</span>}
      </div>
      {link.note && (
        <div className="mt-1" style={mono(10, "#475569")} data-testid={`freight-link-note-${link.id}`}>
          <ArrowRight size={9} className="inline mr-0.5" /> {link.note}
        </div>
      )}
    </div>
  );
};

/* ─── Top-level panel ──────────────────────────────────────────────── */

export const LinkedTripsPanel = ({ rid }) => {
  const { data, isLoading } = useFreightLinks(rid);
  const create = useCreateFreightLink();
  const [picking, setPicking] = useState(false);
  const [pendingNote, setPendingNote] = useState("");
  const [pendingLinkType, setPendingLinkType] = useState("paired");
  const [pendingDeal, setPendingDeal] = useState(null);
  const items = data?.items || [];

  const onPick = (d) => setPendingDeal(d);
  const submit = async () => {
    if (!pendingDeal) return;
    try {
      await create.mutateAsync({
        from_request_id: rid,
        to_request_id: pendingDeal.id,
        link_type: pendingLinkType,
        note: pendingNote || null,
      });
      toast.success(`Linked to ${pendingDeal.deal_name || pendingDeal.id}`);
      setPicking(false); setPendingDeal(null); setPendingNote(""); setPendingLinkType("paired");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    }
  };

  return (
    <section
      className="rounded-md p-4 mt-4"
      style={{ background: "var(--bg-surface, #fff)", border: "1px solid rgba(126,58,168,0.30)" }}
      data-testid="linked-trips-panel"
    >
      <header className="flex items-center justify-between mb-3">
        <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15 }}>
          <Link2 size={14} className="inline mr-1" color="#7e3aa8" />
          Linked trips ({items.length})
        </h2>
        {!picking && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            style={{ ...mono(10, "#7e3aa8", 700, "0.6px"), textTransform: "uppercase", background: "rgba(126,58,168,0.10)", border: "1px solid rgba(126,58,168,0.30)", padding: "5px 10px", borderRadius: 5, cursor: "pointer" }}
            data-testid="add-freight-link-btn"
          >
            <Plus size={11} className="inline mr-1" /> Link a trip
          </button>
        )}
      </header>

      {picking && (
        <div className="mb-3">
          {!pendingDeal ? (
            <DealPicker
              excludeId={rid}
              onPick={onPick}
              onClose={() => { setPicking(false); setPendingDeal(null); }}
            />
          ) : (
            <div className="rounded p-3" style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.30)" }} data-testid="link-confirm">
              <div className="flex items-center gap-2 mb-2">
                <strong style={mono(11, "#7e3aa8", 700, "0.6px")}>CONFIRM LINK →</strong>
                <span style={mono(12)}>{pendingDeal.deal_name || pendingDeal.id}</span>
                <StagePill stage={pendingDeal.deal_stage} />
                <button type="button" onClick={() => setPendingDeal(null)} className="ml-auto" style={{ background: "transparent", border: "none", color: "#6b7388", cursor: "pointer" }}><X size={14} /></button>
              </div>
              <div style={mono(10, "#6b7388")} className="mb-2">
                {_fmtRoute({ pickup: (pendingDeal.pickups || [{}])[0], dropoff: (pendingDeal.dropoffs || [{}])[0] })}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <label className="block">
                  <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Link type</div>
                  <select value={pendingLinkType} onChange={(e) => setPendingLinkType(e.target.value)} style={inputStyle} data-testid="link-type-select">
                    <option value="paired">Paired (same freight, two legs)</option>
                    <option value="related">Related (same customer / ref)</option>
                  </select>
                </label>
                <label className="block col-span-2">
                  <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Note (optional)</div>
                  <input value={pendingNote} onChange={(e) => setPendingNote(e.target.value)} placeholder="e.g. round-trip, same trailer" style={inputStyle} data-testid="link-note-input" />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setPicking(false); setPendingDeal(null); }}
                        style={{ ...mono(10, "#6b7388"), background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "6px 12px", borderRadius: 4, cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="button" onClick={submit} disabled={create.isPending}
                        style={{ ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#7e3aa8", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 4, cursor: "pointer", opacity: create.isPending ? 0.6 : 1 }}
                        data-testid="link-confirm-submit">
                  {create.isPending ? <Loader2 size={11} className="animate-spin inline mr-1" /> : <Link2 size={11} className="inline mr-1" />}
                  Create link
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isLoading && <div style={mono(11, "#6b7388")}>Loading links…</div>}
      {!isLoading && items.length === 0 && !picking && (
        <div className="text-center py-3" style={mono(11, "#6b7388")} data-testid="linked-trips-empty">
          No linked trips yet. Click <strong>Link a trip</strong> to pair this deal with another leg.
        </div>
      )}
      {items.map((link) => <LinkRow key={link.id} link={link} />)}
    </section>
  );
};

export default LinkedTripsPanel;
